import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageSeo from "@/components/PageSeo";
import { useLanguage } from "@/i18n/LanguageContext";
import type { TranslationKey } from "@/i18n/translations";


const BOOKING_URL = "https://calendar.app.google/smFTnDe7izrtAPVg6";

const FounderReadiness = () => {
  const { t } = useLanguage();

  const approach: { titleKey: TranslationKey; bodyKey: TranslationKey }[] = [
    { titleKey: "readiness.approach.capture.title", bodyKey: "readiness.approach.capture.body" },
    { titleKey: "readiness.approach.reframe.title", bodyKey: "readiness.approach.reframe.body" },
    { titleKey: "readiness.approach.pressure.title", bodyKey: "readiness.approach.pressure.body" },
    { titleKey: "readiness.approach.perform.title", bodyKey: "readiness.approach.perform.body" },
  ];

  const outcomes: TranslationKey[] = [
    "readiness.outcomes.li1",
    "readiness.outcomes.li2",
    "readiness.outcomes.li3",
    "readiness.outcomes.li4",
  ];

  const audience: TranslationKey[] = [
    "readiness.audience.li1",
    "readiness.audience.li2",
    "readiness.audience.li3",
  ];

  return (
    <main className="min-h-screen bg-background">
      <PageSeo
        title={t("seo.readiness.title")}
        description={t("seo.readiness.description")}
        path="/founder-readiness"
      />
      <Navigation />


      {/* Hero */}
      <section className="pt-40 pb-28 bg-gradient-to-br from-primary via-foreground/95 to-foreground/90">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center text-white space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent-mint">
              {t("readiness.hero.stage")}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              {t("readiness.hero.title")}
            </h1>
            <div className="space-y-5 text-white/80 font-serif text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
              <p>
                {t("readiness.hero.body1")}
                <br />
                {t("readiness.hero.body1b")}
              </p>
              <p>
                {t("readiness.hero.body2")}
              </p>
            </div>
            <div className="pt-4">
              <Button
                variant="hero"
                size="xl"
                onClick={() => window.open(BOOKING_URL, "_blank")}
                className="group font-sans"
              >
                {t("readiness.hero.cta")}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("readiness.problem.label")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("readiness.problem.title")}
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground font-serif leading-relaxed">
              <p>{t("readiness.problem.p1")}</p>
              <p>{t("readiness.problem.p2")}</p>
              <p>{t("readiness.problem.p3")}</p>
              <ul className="space-y-3 pl-6 list-disc marker:text-accent">
                <li>{t("readiness.problem.li1")}</li>
                <li>{t("readiness.problem.li2")}</li>
                <li>{t("readiness.problem.li3")}</li>
              </ul>
              <p>{t("readiness.problem.p4")}</p>
              <p className="text-foreground font-medium">
                {t("readiness.problem.p5")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="space-y-6 max-w-3xl">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("readiness.approach.label")}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                {t("readiness.approach.title")}
              </h2>
              <p className="text-lg text-muted-foreground font-serif leading-relaxed">
                {t("readiness.approach.p1")}
              </p>
              <p className="text-base text-muted-foreground font-serif">
                {t("readiness.approach.p2")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-px bg-border/60 border border-border/60">
              {approach.map((step, i) => (
                <div key={step.titleKey} className="bg-background p-8 sm:p-10 space-y-4">
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs font-mono text-accent tracking-widest">
                      0{i + 1}
                    </span>
                    <h3 className="text-2xl font-bold tracking-tight">{t(step.titleKey)}</h3>
                  </div>
                  <p className="text-muted-foreground font-serif leading-relaxed">{t(step.bodyKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What You Leave With */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("readiness.outcomes.label")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("readiness.outcomes.title")}
            </h2>
            <p className="text-lg text-muted-foreground font-serif leading-relaxed">
              {t("readiness.outcomes.intro")}
            </p>
            <ul className="space-y-4">
              {outcomes.map((o) => (
                <li
                  key={o}
                  className="flex gap-4 text-lg text-foreground/90 font-serif border-b border-border/40 pb-4"
                >
                  <span className="text-accent font-mono text-sm pt-1.5">·</span>
                  <span>{t(o)}</span>
                </li>
              ))}
            </ul>
            <p className="text-base text-muted-foreground font-serif italic pt-2">
              {t("readiness.outcomes.footer")}
            </p>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("readiness.audience.label")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("readiness.audience.title")}
            </h2>
            <p className="text-lg text-muted-foreground font-serif">{t("readiness.audience.intro")}</p>
            <ul className="space-y-4">
              {audience.map((a) => (
                <li key={a} className="flex gap-4 text-lg text-foreground/90 font-serif">
                  <span className="text-accent font-mono text-sm pt-1.5">→</span>
                  <span>{t(a)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Transition */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("readiness.transition.label")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("readiness.transition.title")}
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground font-serif leading-relaxed">
              <p>{t("readiness.transition.p1")}</p>
              <p>{t("readiness.transition.p2")}</p>
              <p>
                {t("readiness.transition.p3")}
              </p>
            </div>
            <div className="pt-4">
              <Link
                to="/capital-raising"
                className="text-sm font-medium text-foreground link-underline font-sans inline-flex items-center gap-2"
              >
                {t("readiness.transition.link")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-gradient-to-br from-primary via-foreground/95 to-foreground/90 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("readiness.cta.title")}
            </h2>
            <p className="text-lg text-white/75 font-serif leading-relaxed max-w-2xl mx-auto">
              {t("readiness.cta.body")}
            </p>
            <Button
              variant="hero"
              size="xl"
              onClick={() => window.open(BOOKING_URL, "_blank")}
              className="group font-sans"
            >
              {t("readiness.cta.button")}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-sm text-white/50 font-serif italic pt-8 leading-relaxed">
              {t("readiness.cta.footer1")}
              <br />
              {t("readiness.cta.footer2")}
              <br />
              {t("readiness.cta.footer3")}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default FounderReadiness;
